import React, { useState } from 'react';
import {
  AlertTriangle, Settings, Bell, FileText, ArrowDownCircle, ArrowUpCircle,
  Percent, FileCheck, FileMinus, CreditCard, Landmark, Wallet, Receipt
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

import ActiveAccountingWarnings from './ActiveAccountingWarnings';
import AccountingInvoiceWarningSettings from './AccountingInvoiceWarningSettings';
// Uyarı kategorileri
const WARNING_CATEGORIES = [
  { key: 'alinacak-faturalar', label: 'Alınacak Faturalar', icon: FileText },
  { key: 'kesilecek-faturalar', label: 'Kesilecek Faturalar', icon: Receipt },
  { key: 'alacak-takip', label: 'Alacak Takip', icon: ArrowDownCircle },
  { key: 'borc-takip', label: 'Borç Takip', icon: ArrowUpCircle },
  { key: 'alinan-cek', label: 'Alınan Çek', icon: FileCheck },
  { key: 'verilen-cek', label: 'Verilen Çek', icon: FileMinus },
  { key: 'kredi-kartlari', label: 'Kredi Kartları', icon: CreditCard },
  { key: 'banka-hesaplari', label: 'Banka Hesapları', icon: Landmark },
  { key: 'nakit-kasalar', label: 'Nakit Kasalar', icon: Wallet },
];

const AccountingWarnings = () => {
  const [activeTab, setActiveTab] = useState('uyarilar');
  const [activeCategory, setActiveCategory] = useState(WARNING_CATEGORIES[0].key);
  const [activeSettingsCategory, setActiveSettingsCategory] = useState(WARNING_CATEGORIES[0].key);

  return (
    <div className="space-y-6" data-testid="accounting-warnings-page">
      {/* Başlık */}
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100">
          <AlertTriangle className="h-6 w-6 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Uyarılar</h1>
          <p className="text-sm text-gray-500">Muhasebe uyarıları ve uyarı ayarlamaları</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="uyarilar" data-testid="tab-uyarilar" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Uyarılar
          </TabsTrigger>
          <TabsTrigger value="ayarlamalar" data-testid="tab-uyari-ayarlamalari" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Uyarı Ayarlamaları
          </TabsTrigger>
        </TabsList>

        {/* Uyarılar Sekmesi */}
        <TabsContent value="uyarilar" className="mt-6" data-testid="content-uyarilar">
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
            <TabsList className="flex w-full flex-wrap justify-start gap-2 bg-transparent p-0 h-auto">
              {WARNING_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <TabsTrigger
                    key={cat.key}
                    value={cat.key}
                    data-testid={`tab-warning-${cat.key}`}
                    className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm data-[state=active]:border-indigo-300 data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700"
                  >
                    <Icon className="h-4 w-4" />
                    {cat.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {WARNING_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <TabsContent key={cat.key} value={cat.key} className="mt-6" data-testid={`content-warning-${cat.key}`}>
                  {cat.key === 'alinacak-faturalar' || cat.key === 'kesilecek-faturalar' ? (
                    <ActiveAccountingWarnings ruleType={cat.key} />
                  ) : (
                    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
                      <Icon className="mb-3 h-10 w-10 text-gray-300" />
                      <p className="text-base font-medium text-gray-700">{cat.label}</p>
                      <p className="mt-1 text-sm text-gray-400">
                        Bu kategori için uyarı ayarlamaları tanımlandıkça, eşleşen uyarılar burada listelenecek.
                      </p>
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        </TabsContent>

        {/* Uyarı Ayarlamaları Sekmesi */}
        <TabsContent value="ayarlamalar" className="mt-6" data-testid="content-uyari-ayarlamalari">
          <Tabs value={activeSettingsCategory} onValueChange={setActiveSettingsCategory} className="w-full">
            <TabsList className="flex w-full flex-wrap justify-start gap-2 bg-transparent p-0 h-auto">
              {WARNING_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <TabsTrigger
                    key={cat.key}
                    value={cat.key}
                    data-testid={`tab-settings-${cat.key}`}
                    className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm data-[state=active]:border-indigo-300 data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700"
                  >
                    <Icon className="h-4 w-4" />
                    {cat.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {WARNING_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <TabsContent key={cat.key} value={cat.key} className="mt-6" data-testid={`content-settings-${cat.key}`}>
                  {cat.key === 'alinacak-faturalar' || cat.key === 'kesilecek-faturalar' ? (
                    <AccountingInvoiceWarningSettings ruleType={cat.key} title={cat.label} />
                  ) : (
                    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
                      <Icon className="mb-3 h-10 w-10 text-gray-300" />
                      <p className="text-base font-medium text-gray-700">{cat.label} — Ayarlar</p>
                      <p className="mt-1 text-sm text-gray-400">Bu kategori için uyarı ayarları yakında tanımlanacak.</p>
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AccountingWarnings;
